import React, { useState } from 'react';
import { Box, Button, Checkbox, Flex, Input, InputGroup, InputLeftElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { Icon } from "../../components/icon";
import { localisable } from "../../generic/localisable";
import { formatBalance, formatDate } from "../../utils/constant";
import { CARD_ICONS, amountInputProps, checkboxStyle, commonInputStyleProps, emailFormButtonProps, existingCardDetailsText, formMainContainerCommonProps, innerTextStyles, radioButtonStyle, tableThProps } from "./constants";
import GlobalConstants from "../../constants/GlobalConstants";
import { FormField } from "../../components/form";
import { PayRentPropTypes } from "./PayRentPropTypes";
import { Link, navigate } from "gatsby-link";

export const renderErrorContainer = (error: string | undefined, setStatus: (value: boolean) => void) => {

    const closeErrorContainer = () => {
        setStatus(false);
    };

    return (
        <Flex alignItems='center' gap='8px' bg='background.error' padding='4px 16px' borderRadius='4px' justifyContent='space-between' w='fit-content'>
            <Text textStyle='body1' color='text.error' padding='8px'>{error}</Text>
            <Box cursor='pointer' color='text.grey'>
                <Icon
                    size={'md'}
                    className='icon-tfw-clear'
                    onClick={closeErrorContainer}
                />
            </Box>
        </Flex>
    )
};


export const RenderBalanceDetailsModal = ({ Balance, UnpaidAr, LastPaymentAmount, LastPaymentOn, isModalOpen, setModalOpen }: PayRentPropTypes.TBalanceDetailsProps) => {

    const handleModalClose = () => {
        setModalOpen(false);
    };

    const renderUnpaidArTable = () => {
        return (
            <Flex>
                {
                    !!UnpaidAr.length && <TableContainer >
                        <Table
                            variant='none'
                            style={{ display: 'block', height: '60vh', paddingRight: '8px', overflowY: 'scroll', scrollbarWidth: 'thin' }}
                            sx={{
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                    borderRadius: '8px',
                                    backgroundColor: `rgba(0, 0, 0, 0.05)`,
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: `rgba(0, 0, 0, 0.05)`,
                                },
                            }}
                        >
                            <Thead>
                                <Tr gap='12px' padding='0px'>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles} w='max-content'>{localisable.fromDate}</Text></Th>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles} w='max-content'>{localisable.toDate}</Text></Th>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles}>{localisable.description}</Text></Th>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles}>{localisable.amount}</Text></Th>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles} w='max-content'>{localisable.amountOwed}</Text></Th>
                                    <Th {...tableThProps} textAlign='center' whiteSpace='normal'><Text {...innerTextStyles}>{localisable.comments}</Text></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {
                                    UnpaidAr.map(({ CtrlNum, FromDate, ToDate, Description, Amount, AmountOwed, Comments }: Record<string, any>) =>
                                        <Tr key={CtrlNum} gap='12px' padding='0px' borderBottom='1px solid' borderColor='common.grey05'>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{formatDate(FromDate)}</Text>
                                            </Td>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{formatDate(ToDate)}</Text>
                                            </Td>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{Description}</Text>
                                            </Td>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{`$${Amount.toFixed(2)}`}</Text>
                                            </Td>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{`$${Math.abs(AmountOwed).toFixed(2)}`}</Text>
                                            </Td>
                                            <Td {...tableThProps} textAlign='center' whiteSpace='normal'>
                                                <Text textStyle='body3'>{Comments}</Text>
                                            </Td>
                                        </Tr>
                                    )
                                }
                            </Tbody>
                        </Table>
                    </TableContainer>
                }
            </Flex>
        )
    };

    const renderModalBody = () => {
        return (
            <Flex direction='column' gap='16px'>
                <Flex direction='column' gap='4px'>
                    <Flex gap='4px'>
                        <Text textStyle='body3' fontWeight='700'>{localisable.balance}:</Text>
                        <Text textStyle='body3'>{formatBalance(Balance)}</Text>
                    </Flex>
                    <Flex gap='4px'>
                        <Text textStyle='body3' fontWeight='700'>{localisable.lastPaymentReceived}:</Text>
                        <Text textStyle='body3'>{formatDate(LastPaymentOn)}</Text>
                    </Flex>
                    <Flex gap='4px'>
                        <Text textStyle='body3' fontWeight='700'>{localisable.lastPaymentAmount}:</Text>
                        <Text textStyle='body3'>{formatBalance(LastPaymentAmount)}</Text>
                    </Flex>
                </Flex>
                <Text pb='8px' textStyle='subtitle' borderBottom='1px solid' borderColor={'common.grey05'}>{localisable.detail}:</Text>
                {renderUnpaidArTable()}
            </Flex>
        )
    }

    return (
        <div>
            <Modal closeOnOverlayClick={false} isOpen={isModalOpen} onClose={handleModalClose} size={'xl'}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text pb='8px' textStyle='h4' borderBottom='1px solid' borderColor={'common.grey05'} >{localisable.balanceDetail}</Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {renderModalBody()}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    )
};

export const renderDropDown = (
    isForm: boolean,
    currentFormProps: any | undefined,
    cardList: { [key: string]: any }[],
    shallRenderCardIcons: boolean,
    selectedPaymentMode: string | undefined,
    setSelectedPaymentMode: React.Dispatch<React.SetStateAction<string>>,
    totalAmount: number,
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>,
    handleSurchargeOrDiscountCalculator?: (var1: number, var2: number) => Promise<void> | undefined,
    setIsSurchargeOrDiscount?: React.Dispatch<React.SetStateAction<boolean>>,
) => {

    const { isDarkTheme } = GlobalConstants.getInstance().getConstants();

    const onPaymentMethodChange = async (event: any) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const payChargeDescription = selectedOption.getAttribute('data-pay');
        setIsLoading && setIsLoading(true);
        setSelectedPaymentMode(event.target.value);
        setIsSurchargeOrDiscount && setIsSurchargeOrDiscount(payChargeDescription === "true" ? true : false)
        if (handleSurchargeOrDiscountCalculator) {
            await handleSurchargeOrDiscountCalculator(totalAmount, event.target.value);
        }
    };

    return (
        <Stack gap='6px' w={{ base: '100%', sm: '318px' }} >
            <Flex justifyContent='space-between'>
                <Text textStyle='body1'>{`${localisable.cardType}*`}</Text>
                {
                    shallRenderCardIcons && <Flex gap='16px' justifyContent='center'>
                        {
                            cardList.map(({ PayTypeDescription }, index) => {
                                return <Icon key={index} size={'default'} className={CARD_ICONS[PayTypeDescription]} />
                            })
                        }
                    </Flex>
                }
            </Flex >
            <Select value={isForm ? currentFormProps.values.cardType : selectedPaymentMode} onChange={onPaymentMethodChange} {...commonInputStyleProps} padding='inherit'>
                <option value="" disabled selected hidden color='input.placeholder'>
                    {localisable.selectCardType}
                </option>
                {!!cardList.length && cardList.map(({ PayTypeDescription, PayTypeID, PayChargeEnabled }, index: number) => (
                    <option key={index} data-pay={PayChargeEnabled} value={PayTypeID} style={{ backgroundColor: `${isDarkTheme ? 'black' : 'white'}` }}>
                        {PayTypeDescription}
                    </option>
                ))}
            </Select>
        </Stack >)
};

export const renderAmountRadioGroup = (
    isForm: boolean,
    amountType: string | undefined,
    setAmountType: React.Dispatch<React.SetStateAction<string>>,
    Balance: number,
    setTotalAmount: React.Dispatch<React.SetStateAction<number>>,
    amount: string | undefined,
    setAmount: React.Dispatch<React.SetStateAction<string>>,
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    selectedPaymentMode: string,
    inputProps: Record<string, any> | undefined,
    currentFormProps: any | undefined,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    PreferredPaymentMethod: number,
    handleSurchargeOrDiscountCalculator?: (var1: number, var2: number) => Promise<void> | undefined,
    isSurchargeOrDiscount?: boolean,
    setIsPaymentButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>,
    setTotalPaymentAmount?: React.Dispatch<React.SetStateAction<number>>,
) => {

    const onAmountRadioButtonChange = (newValue: string) => {
        setAmountType(newValue);
        if (newValue === 'balanceDue') {
            setTotalAmount(Balance);
            if (isForm) {
                currentFormProps.form.change('amountToBeConsidered', '');
            } else {
                setAmount('');
            }
        }
        else{
            setTotalAmount(0);
        }
    };

    const handleAmountChange = async (event: any) => {
        if (!isForm) {
            setAmount(event.target.value);
        }
    };

    const handleClickAway = async (event: any) => {
        if (amount) {
            setAmount(Number(amount).toFixed(2));
        }
        setTotalAmount(event.target.value);
        if (isSurchargeOrDiscount && isSurchargeOrDiscount) {
            setIsLoading(true);
            if (handleSurchargeOrDiscountCalculator) {
                await handleSurchargeOrDiscountCalculator(event.target.value, Number(selectedPaymentMode) || PreferredPaymentMethod || 0);
            }
        }
        else {
            setIsPaymentButtonDisabled && setIsPaymentButtonDisabled(false);
            const value = parseFloat(parseFloat(event.target.value).toFixed(2));
            setTotalPaymentAmount && setTotalPaymentAmount(value);
        }
        setIsPaymentButtonDisabled && setIsPaymentButtonDisabled(false)
    };

    const handleBalanceDetailClick = () => {
        setModalOpen(true);
    };

    return (
        <Flex gap='24px' w='100%'>
            <Text textStyle='body1' w='fit-content'>{localisable.amount}*</Text>
            <RadioGroup onChange={onAmountRadioButtonChange} value={amountType} w='-webkit-fill-available'>
                <Flex direction='column' gap='16px'>
                    <Flex gap='8px' flexWrap='wrap'>
                        <Radio value="balanceDue" {...radioButtonStyle} isDisabled={Balance < 0}>
                            <Text textStyle='body1'>{localisable.balanceDue}</Text>
                        </Radio>
                        {!!Balance && <Text textStyle='body1'>{formatBalance(Balance)}</Text>}
                        <Text textStyle='body1' cursor='pointer' color='button.blue' onClick={handleBalanceDetailClick}>{localisable.balanceDetail}</Text>
                    </Flex>
                    <Flex alignItems='center' gap='24px' flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                        <Radio value="otherAmount" {...radioButtonStyle}>
                            <Text textStyle='body1' minW='100px'>{localisable.otherAmount}</Text>
                        </Radio>
                        <InputGroup border='none' w="fit-content">
                            <InputLeftElement h='35px'><Text textStyle='body1' alignItems='end'>$</Text></InputLeftElement>
                            {
                                isForm ?
                                    <FormField
                                        {...inputProps}
                                        {...currentFormProps}
                                        onBlur={handleClickAway}
                                        isDisabled={!(amountType === 'otherAmount')}
                                    >
                                        <Input pl='40px' />
                                    </FormField> :
                                    <Input
                                        type='number'
                                        textStyle='body1'
                                        isDisabled={!(amountType === 'otherAmount')}
                                        value={amount}
                                        onChange={(event) => {
                                            handleClickAway(event);
                                            handleAmountChange(event);
                                        }}
                                        {...amountInputProps} />
                            }
                        </InputGroup>
                    </Flex>
                </Flex>
            </RadioGroup >
        </Flex >
    )
};

export const renderCardRadioButtonSelection = (
    cardOpted: string,
    setCardOpted: React.Dispatch<React.SetStateAction<string>>,
    totalAmount: number,
    setSelectedPaymentMode: React.Dispatch<React.SetStateAction<string>>,
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>,
    handleSurchargeOrDiscountCalculator?: (var1: number, var2: number) => Promise<void> | undefined,
    PreferredPaymentMethod?: number,
    existingCardDetails?: { [key: string]: string }
) => {

    const onCardRadioButtonChange = async (newValue: string) => {
        setIsLoading && setIsLoading(true);
        setCardOpted(newValue);
        if (newValue === 'existingCard') {
            if (PreferredPaymentMethod) {
                setSelectedPaymentMode(PreferredPaymentMethod.toString());
                if (handleSurchargeOrDiscountCalculator) {
                    await handleSurchargeOrDiscountCalculator(totalAmount, PreferredPaymentMethod);
                }
            }
        } else {
            setSelectedPaymentMode('')
            setIsLoading && setIsLoading(false);
        }
    };

    return (
        <RadioGroup onChange={onCardRadioButtonChange} value={cardOpted}>
            <Flex gap='16px'>
                {(!!existingCardDetails && !!existingCardDetails.CCNumber) && (
                <Radio value="existingCard" {...radioButtonStyle}>
                    <Text textStyle='body1'>{localisable.useExistingCard}</Text>
                </Radio>
                ) }
                <Radio value="newCard" {...radioButtonStyle}>
                    <Text textStyle='body1'>{localisable.useNewCard}</Text>
                </Radio>
            </Flex>
        </RadioGroup>
    )
};

export const renderExistingCardDetails = (existingCardDetails: { [key: string]: string }) => {
    const { CCNumber, CCBillingAccountName, CCExpirationMonth, CCExpirationYear, CCBillingAddress, CCBillingZIP } = existingCardDetails || {};
    // const { ACHAccountNumber, ACHRoutingNumber } = existingAchDetails || {};
    return (
        <Flex direction='column'>
            {
                !!existingCardDetails && Object.keys(existingCardDetails).length && <Flex direction='column' w={{ base: 'inherit', sm: 'fit-content' }} bg='tabsHeader.active' padding='4px'>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.accountNumber}:</Text>
                        <Text {...existingCardDetailsText}>{`************${CCNumber}`}</Text>
                    </Flex>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.accountName}:</Text>
                        <Text {...existingCardDetailsText}>{CCBillingAccountName}</Text>
                    </Flex>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.expirationMonth}:</Text>
                        <Text {...existingCardDetailsText}>{CCExpirationMonth}</Text>
                    </Flex>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.expirationYear}:</Text>
                        <Text {...existingCardDetailsText}>{CCExpirationYear}</Text>
                    </Flex>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.accountBillingAddress}:</Text>
                        <Text {...existingCardDetailsText}>{CCBillingAddress}</Text>
                    </Flex>
                    <Flex>
                        <Text textStyle='body1' minW={{ base: '50%', sm: '175px' }}>{localisable.billingZip}:</Text>
                        <Text {...existingCardDetailsText}>{CCBillingZIP}</Text>
                    </Flex>
                </Flex>
            }
        </Flex>
    );
};

export const generateYearArray = () => {
    let year = [];
    let newYear = new Date().getFullYear(); // Get the current year
    for (let i = 1; i < 15; i++) {
        year.push({
            id: newYear.toString().slice(-2), // Get the last two digits of the year label: newYear, // Store the full year 
        });
        newYear = newYear + 1; // Increment to the next year
    }
    return year;
};

export const renderExpirationDropdown = (dataList: { [key: string]: any }[], defaultValue: string) => {
    const { isDarkTheme } = GlobalConstants.getInstance().getConstants();
    return (
        <Flex>
            <Select value={defaultValue}{...commonInputStyleProps} padding='inherit'>
                <option value="" disabled hidden color='input.placeholder'>
                    {localisable.selectMonth}
                </option>
                {!!dataList.length && dataList.map(({ id, label }, index: number) => (
                    <option key={index} value={id} style={{ backgroundColor: `${isDarkTheme ? 'black' : 'white'}` }}>
                        {label ? label : id}
                    </option>
                ))}
            </Select>
        </Flex >
    )
}

export const renderEnableAutoPayCheckbox = (
    description: string,
    IsAutoPaymentsForced: boolean | undefined,
    IsOptingOutAutoPaymentRestricted: boolean | undefined,
    defaultValue: boolean,
    setSignUpForAutopaySelection?: any) => {
    const [isChecked, setIsChecked] = useState(defaultValue);
    return (
        <Flex alignItems='baseline' gap='16px' maxW={{ base: '100%', sm: '480px' }}>
            <Checkbox
                isChecked={!!isChecked}
                isDisabled={!!IsAutoPaymentsForced || (IsOptingOutAutoPaymentRestricted && defaultValue)}
                onChange={(e) => {
                    setIsChecked(e.target.checked);
                    if (setSignUpForAutopaySelection) {
                        setSignUpForAutopaySelection(e.target.checked);
                    }
                }}
                {...checkboxStyle}
            />
            <Text textStyle='body1'>{description}</Text>
        </Flex>
    )
};


export const SignUpSuccessComponent = ({ customerNote }: PayRentPropTypes.TSignUpSuccessProps) => {

    return (
        <Flex direction='column' {...formMainContainerCommonProps}>
            <Flex direction='column' gap='8px'>
                <Text textStyle='subtitle' fontStyle='italic'>{localisable.signUpSuccess}</Text>
                <Text textStyle='subtitle' fontStyle='italic'>{customerNote}</Text>
            </Flex>
            <Flex direction={{ base: 'column', sm: 'row' }} gap='24px' justifyContent={{ sm: 'flex-end' }} alignItems={'center'}>
                <Button
                    {...emailFormButtonProps}
                    onClick={() => navigate('/pay-rent/rent-sub/login')}
                    bg='button.blue'
                    minW={{ base: '100%', sm: '204px' }}
                >
                    <Text textStyle='buttonSmall' color='common.white' >{localisable.logOff}</Text>
                </Button>
                <Link to='/'>
                    <Text textStyle='buttonSmall' color='button.blue' textDecoration='underline'>{localisable.goToHome}</Text>
                </Link>
            </Flex>
        </Flex>
    )
}


export const PaymentSuccessComponent = ({ paidAmount, confirmationNumber, logOutHandler, customerNote }: PayRentPropTypes.TPayRentSuccessProps) => {
    const CONSTANT_CUSTOMER_NOTE = 'Thank You'
    return (
        <Flex direction='column' {...formMainContainerCommonProps}>
            <Text textStyle='h2'>{localisable.thankyou}!</Text>
            <Flex direction='column' gap='8px'>
                <Text textStyle='body1'>{`Your Payment of $${paidAmount?.toFixed(2)} was successful!`}</Text>
                <Text textStyle='body1' >{`Your confirmation number is ${confirmationNumber}`}</Text>
                <Text textStyle='body1' >{customerNote || CONSTANT_CUSTOMER_NOTE}</Text>
            </Flex>
            <Flex direction={{ base: 'column', sm: 'row' }} gap='24px' justifyContent={{ sm: 'flex-end' }} alignItems={'center'}>
                <Button
                    {...emailFormButtonProps}
                    onClick={logOutHandler}
                    bg='brand.lightTertiary'
                    minW={{ base: '100%', sm: '204px' }}
                >
                    <Text textStyle='buttonSmall' color='brand.secondary' >{localisable.logOff}</Text>
                </Button>
                <Link to='/'>
                    <Text textStyle='buttonSmall' color='brand.lightTertiary' textDecoration='underline'>{localisable.goToHome}</Text>
                </Link>
            </Flex>
        </Flex>
    )
}

export const AutoPayChangeSuccessComponent = ({ message, logOutHandler }: PayRentPropTypes.TAutoPayChangeSuccessProps) => {
    return (
        <Flex direction='column' {...formMainContainerCommonProps}>
            <Text textStyle='h2'>{localisable.thankyou}!</Text>
            <Flex direction='column' gap='8px'>
                <Text textStyle='body1'>{message}</Text>
            </Flex>
            <Flex direction={{ base: 'column', sm: 'row' }} gap='24px' justifyContent={{ sm: 'flex-end' }} alignItems={'center'}>
                <Button
                    {...emailFormButtonProps}
                    onClick={logOutHandler}
                    bg='brand.lightTertiary'
                    minW={{ base: '100%', sm: '204px' }}
                >
                    <Text textStyle='buttonSmall' color='brand.secondary' >{localisable.logOff}</Text>
                </Button>
                <Link to='/'>
                    <Text textStyle='buttonSmall' color='brand.lightTertiary' textDecoration='underline'>{localisable.goToHome}</Text>
                </Link>
            </Flex>
        </Flex>
    )
};

export const renderStateDropdown = (dataList: { [key: string]: any }[], defaultValue: string) => {
    const { isDarkTheme } = GlobalConstants.getInstance().getConstants();
    return (
        <Flex>
            <Select value={defaultValue}{...commonInputStyleProps} padding='inherit'>
                <option value="" disabled hidden color='input.placeholder'>
                    {localisable.selectMonth}
                </option>
                {!!dataList.length && dataList.map(({ id, description }, index: number) => (
                    <option key={index} value={id} style={{ backgroundColor: `${isDarkTheme ? 'black' : 'white'}` }}>
                        {description}
                    </option>
                ))}
            </Select>
        </Flex >
    )
}

export const displayLoader = () => (
    <Box display="flex" alignItems="center" justifyContent="center" flex="1" position='absolute' top='45%' left={0} right={0} zIndex={2}>
        <Spinner size='xl' />
    </Box>
)

export const errorContainer = (errorObject: any) => (
    <Flex direction='column' gap='16px'>
        <Text fontStyle='italic' textStyle='subtitle' color='red'>There was an Error</Text>
        <Text fontStyle='italic' textStyle='subtitle' color='red'>Response Code: {errorObject.errorCode}</Text>
        <Text fontStyle='italic' textStyle='subtitle' color='red'>Response Description:{errorObject.errorMessage}</Text>
    </Flex>
);